/**
 * Starts a connector's OAuth flow: fetch the provider's authorization URL and
 * send the browser there. Shared by the Data Sources page and the Files
 * screens' "Connect a source" dialog so both offer the same providers.
 */
import { useCallback } from "react";
import type { DataSource } from "../config/dataSources";
import { useToast } from "../context/ToastContext";
import {
	getBoxAuthUrl,
	getDropboxAuthUrl,
	getSharePointAuthUrl,
} from "../services/integrationApi";
import { friendlyErrorMessage } from "../utils/apiErrorDetail";

const AUTH_URL_BY_SOURCE: Record<string, () => Promise<{ auth_url: string }>> = {
	box: getBoxAuthUrl,
	dropbox: getDropboxAuthUrl,
	sharepoint: getSharePointAuthUrl,
};

export function useConnectDataSource(): (dataSource: DataSource) => Promise<void> {
	const { showToast } = useToast();

	return useCallback(
		async (dataSource: DataSource) => {
			const getAuthUrl = AUTH_URL_BY_SOURCE[dataSource.id];
			if (!getAuthUrl) {
				// No OAuth handler for this provider yet. Say so, rather than
				// navigating somewhere with nothing connected.
				showToast(
					`${dataSource.name} isn't available to connect yet. Upload files directly, or connect Box, Dropbox, or SharePoint.`,
					"info",
				);
				return;
			}
			try {
				const { auth_url } = await getAuthUrl();
				window.location.href = auth_url;
			} catch (error) {
				console.error(
					`Failed to get ${dataSource.name} authorization URL:`,
					error,
				);
				showToast(
					friendlyErrorMessage(
						error,
						`Couldn't connect to ${dataSource.name}. Please try again.`,
					),
				);
			}
		},
		[showToast],
	);
}
